import type { AgentSessionResponse } from "@/api/generated/types/agentSessionResponse";
import type { InterviewDetail } from "@/api/generated/types/interviewDetail";
import type { InterviewTurn } from "@/api/generated/types/interviewTurn";
import { Header } from "@/components/features/layout/header";
import { AgentLiveClient, type AgentLiveClientDebugState } from "@/lib/agent-live-client";
import { type LiveAudioDebugState, LiveAudioInput } from "@/lib/audio-input";
import {
  type AudioPlaybackDebugState,
  AudioPlaybackQueue,
  type PlaybackState,
} from "@/lib/audio-playback";
import { InterviewRecorder } from "@/lib/interview-recorder";
import { getApiBaseUrl } from "@/lib/runtime-config";
import { PostInterviewCta } from "@/routes/interview/_components/post-interview-cta";
import {
  consumePendingInterviewAutoStart,
  hasPendingInterviewAutoStart,
} from "@/routes/interview/auto-start";
import { useParams } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import {
  type ReactNode,
  type SVGProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

function IconBot(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <rect x="4" y="7" width="16" height="12" rx="3.5" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M12 7V3.5M9.5 13h.01M14.5 13h.01"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="12" cy="3" r="1.3" fill="currentColor" />
      <path
        d="M4 11H2.5M21.5 11H20"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconUser(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <circle cx="12" cy="9" r="4" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M4 20c1-4 4.2-6 8-6s7 2 8 6"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconMic(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <rect x="9" y="3" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M6 11a6 6 0 0 0 12 0M12 17v4"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconEnd(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M5.5 14.5l1.4-2.2a1.5 1.5 0 0 1 .9-.6c2.6-.7 5.8-.7 8.4 0 .4.1.7.3.9.6l1.4 2.2a1.4 1.4 0 0 1-.5 1.9l-1.7 1c-.6.3-1.3.2-1.7-.4l-.8-1.1a1.4 1.4 0 0 0-.8-.5 9 9 0 0 0-3.4 0c-.3.1-.6.3-.8.5l-.8 1.1c-.4.6-1.1.7-1.7.4l-1.7-1a1.4 1.4 0 0 1-.5-1.9z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
        transform="rotate(135 12 14)"
      />
    </svg>
  );
}

function IconCheck(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M5 12.5l4.5 4.5L19 7"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

export type ViewState = "loading" | "ready" | "live" | "completed" | "error";
type CandidateTurnState = "idle" | "listening" | "processing" | "responding";
type TransportRecoveryState = "idle" | "reconnecting" | "failed";
type TransportRecoveryCause =
  | { type: "provider-fatal"; reason: string }
  | { type: "socket-close"; reason: string | null }
  | { type: "socket-error" };

const AI_DISPLAY_NAME = "Relance AI";
const AI_DISPLAY_ROLE = "AI Interviewer";
const DEFAULT_QUESTION_TEXT = "接続が完了すると、現在の質問がここに表示されます。";
const AGENT_TRANSPORT_RECONNECT_DELAYS_MS = [500, 1000, 2000, 4000, 5000] as const;
const DEFAULT_AUDIO_DEBUG_STATE: LiveAudioDebugState = {
  micState: "idle",
  speechActive: false,
  forwardingEnabled: true,
  holdingUntilSpeechStart: false,
  peak: 0,
  rms: 0,
  forwardedChunkCount: 0,
  lastForwardedAt: null,
};
const DEFAULT_AGENT_TRANSPORT_DEBUG_STATE: AgentLiveClientDebugState = {
  wsState: "idle",
  audioFramesReceived: 0,
  audioBytesReceived: 0,
  lastAudioFrameAt: null,
  lastAudioFrameByteLength: null,
  lastMessageKind: "none",
  lastError: null,
};

const DEFAULT_PLAYBACK_DEBUG_STATE: AudioPlaybackDebugState = {
  enqueueCount: 0,
  decodeCount: 0,
  startCount: 0,
  endedCount: 0,
  errorCount: 0,
  lastEnqueuedAt: null,
  lastDecodedAt: null,
  lastStartedAt: null,
  lastEndedAt: null,
  lastErrorAt: null,
  lastEnqueuedDataLength: 0,
  lastDecodedFrameCount: 0,
  lastDecodedDurationMs: 0,
  activeSourceCount: 0,
  audioContextState: "suspended",
};

interface AgentDebugSnapshot {
  browserAudioAttached: boolean;
  lastBrowserAudioAttachedAt: number | null;
  lastBrowserAudioDetachedAt: number | null;
  geminiSetupComplete: boolean;
  bootstrapSent: boolean;
  bootstrapTransport: "client_content_turn" | "realtime_input_text" | null;
  connectAttempt: number;
  bootstrapSentAt: number | null;
  awaitingFirstModelResponse: boolean;
  bootstrapTimedOut: boolean;
  configProfile: string;
  inboundAudioCount: number;
  aiAudioFrameCount: number;
  outboundAudioFrameCount: number;
  outboundAudioBytes: number;
  geminiMessageCount: number;
  lastInboundAudioAt: number | null;
  lastAiAudioFrameAt: number | null;
  lastOutboundAudioAt: number | null;
  lastOutboundAudioByteLength: number | null;
  lastGeminiMessageAt: number | null;
  lastInputTranscriptionAt: number | null;
  lastOutputTranscriptionAt: number | null;
  lastMessageFlags: {
    hasModelTurn: boolean;
    turnComplete: boolean;
    hasInputTranscription: boolean;
    hasOutputTranscription: boolean;
    hasToolCall: boolean;
  };
  pendingCandidateTranscript: string;
  pendingInterviewerTranscript: string;
  candidateActivityState: "idle" | "listening" | "processing";
  lastActivityStartAt: number | null;
  lastActivityEndAt: number | null;
  lastActivitySignalAt: number | null;
  lastGoAwayAt: number | null;
  lastGoAwayTimeLeftMs: number | null;
  resumeHandlePresent: boolean;
  lastResumeHandleUpdatedAt: number | null;
  resumptionState: "idle" | "pending" | "reconnecting";
  dailyBot: {
    state: "idle" | "connected" | "disconnected";
    outboundAudioSentCount: number;
    outboundAudioSentBytes: number;
    outboundAudioBufferedCount: number;
    outboundAudioBufferDroppedCount: number;
    outboundAudioFlushCount: number;
    outboundAudioSendErrorCount: number;
    lastOutboundAudioSentAt: number | null;
    lastOutboundAudioBufferedAt: number | null;
    lastOutboundAudioByteLength: number | null;
    outboundAudioBacklogLength: number;
  } | null;
  lastError: string | null;
}

interface AgentDebugResponse {
  ok: true;
  available: boolean;
  state: "active" | "not-started" | "not-configured" | "unavailable";
  debug: AgentDebugSnapshot | null;
  message?: string;
}

function resolveApiUrl(path: string) {
  const base = getApiBaseUrl();
  return base.length > 0 ? `${base}${path}` : path;
}

function formatDebugTimestamp(value: number | null): string {
  if (!value) return "--";
  return new Date(value).toLocaleTimeString("ja-JP", { hour12: false });
}

function readDebugFlag(): boolean {
  if (typeof window === "undefined") return false;
  const value = new URLSearchParams(window.location.search).get("debug");
  return value === "1" || value === '"1"' || value === "true";
}

async function fetchInterviewDetail(interviewId: string): Promise<InterviewDetail> {
  const response = await fetch(resolveApiUrl(`/api/v1/interviews/${interviewId}`), {
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`detail fetch failed: ${response.status}`);
  }
  return (await response.json()) as InterviewDetail;
}

async function requestAgentSession(interviewId: string): Promise<AgentSessionResponse> {
  const response = await fetch(resolveApiUrl(`/api/v1/interviews/${interviewId}/agent-session`), {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({}),
  });
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(`agent-session failed: ${response.status} ${body}`);
  }
  return (await response.json()) as AgentSessionResponse;
}

async function fetchAgentDebug(interviewId: string): Promise<AgentDebugResponse> {
  const response = await fetch(resolveApiUrl(`/api/v1/interviews/${interviewId}/agent-debug`), {
    credentials: "include",
  });
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(`agent-debug failed: ${response.status} ${body}`);
  }
  return (await response.json()) as AgentDebugResponse;
}

async function postRecording(
  interviewId: string,
  blob: Blob,
  mimeType: string,
  durationMs: number | null
): Promise<void> {
  const extension = mimeType.includes("ogg") ? "ogg" : "webm";
  const formData = new FormData();
  formData.set("recording", new File([blob], `recording.${extension}`, { type: mimeType }));
  if (durationMs !== null) {
    formData.set("durationMs", String(durationMs));
  }
  const response = await fetch(resolveApiUrl(`/api/v1/interviews/${interviewId}/recording`), {
    method: "POST",
    credentials: "include",
    body: formData,
  });
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(
      `recording upload failed: ${response.status}${body.length > 0 ? ` ${body}` : ""}`
    );
  }
}

function readUtmParams(): Record<string, string> {
  return Object.fromEntries(
    [...new URLSearchParams(window.location.search)].filter(([key]) => key.startsWith("utm_"))
  );
}

async function postComplete(interviewId: string, reason = "candidate-ended"): Promise<void> {
  const response = await fetch(resolveApiUrl(`/api/v1/interviews/${interviewId}/complete`), {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ reason, utm: readUtmParams() }),
  });
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(`complete failed: ${response.status}${body.length > 0 ? ` ${body}` : ""}`);
  }
}

function pushInterviewCompletedEventOnce(sentState: { current: boolean }): void {
  if (sentState.current) {
    return;
  }
  sentState.current = true;
  const gtmWindow = window as Window & {
    dataLayer?: Array<{ event: string }>;
  };
  if (!Array.isArray(gtmWindow.dataLayer)) {
    return;
  }
  gtmWindow.dataLayer.push({ event: "interview_completed" });
}

// live 中の生存監視 heartbeat の送信間隔。サーバ側は 60s 単位で書き込みをスロットルするため余裕を持たせる。
const HEARTBEAT_INTERVAL_MS = 40_000;

async function postHeartbeat(interviewId: string, connected: boolean): Promise<void> {
  await fetch(resolveApiUrl(`/api/v1/interviews/${interviewId}/heartbeat`), {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ connected }),
    keepalive: true,
  }).catch(() => undefined);
}

async function postStart(interviewId: string): Promise<void> {
  await fetch(resolveApiUrl(`/api/v1/interviews/${interviewId}/start`), {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: "{}",
  });
}

async function postJoin(interviewId: string): Promise<void> {
  const response = await fetch(resolveApiUrl(`/api/v1/interviews/${interviewId}/join`), {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: "{}",
  });
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(`join failed: ${response.status} ${body}`);
  }
}

function resolveLatestInterviewerTurn(turns: InterviewTurn[]): InterviewTurn | null {
  for (let index = turns.length - 1; index >= 0; index -= 1) {
    const turn = turns[index];
    if (turn?.speaker === "INTERVIEWER") {
      return turn;
    }
  }
  return null;
}

function resolveLatestTranscriptTurn(turns: InterviewTurn[]): InterviewTurn | null {
  for (let index = turns.length - 1; index >= 0; index -= 1) {
    const turn = turns[index];
    if (turn && turn.transcript.trim().length > 0) {
      return turn;
    }
  }
  return null;
}

function resolveCurrentQuestion(turns: InterviewTurn[], questionPlan: string[]): string {
  const latestInterviewerTurn = resolveLatestInterviewerTurn(turns);
  const latestTranscript = latestInterviewerTurn?.transcript.trim();
  if (latestTranscript && latestTranscript.length > 0) {
    return latestTranscript;
  }

  if (
    latestInterviewerTurn?.questionIndex !== null &&
    latestInterviewerTurn?.questionIndex !== undefined
  ) {
    const plannedQuestion = questionPlan[latestInterviewerTurn.questionIndex];
    if (plannedQuestion && plannedQuestion.length > 0) {
      return plannedQuestion;
    }
  }

  return questionPlan[0] ?? DEFAULT_QUESTION_TEXT;
}

function resolveAudioStatusLabel(
  playbackState: PlaybackState,
  connecting: boolean,
  liveActive: boolean,
  audioUnlockRequired: boolean,
  transportRecoveryState: TransportRecoveryState
) {
  if (connecting) {
    return "audio: connecting";
  }

  if (transportRecoveryState === "reconnecting") {
    return "audio: reconnecting";
  }

  if (transportRecoveryState === "failed") {
    return "audio: disconnected";
  }

  if (playbackState === "playing") {
    return "audio: playing";
  }

  if (playbackState === "error") {
    return "audio: error";
  }

  if (liveActive) {
    return "audio: ready";
  }

  if (audioUnlockRequired) {
    return "audio: locked";
  }

  return "audio: idle";
}

function resolveCandidateTurnStatus(
  turnState: CandidateTurnState
): { label: string; className: string } | null {
  if (turnState === "listening") {
    return {
      label: "回答できます",
      className: "rounded-full bg-[#e8f5e4] px-3 py-1 text-xs font-semibold text-[#21551d]",
    };
  }

  if (turnState === "processing") {
    return {
      label: "回答を確認中",
      className: "rounded-full bg-[#eef3ec] px-3 py-1 text-xs font-semibold text-[#3b4b37]",
    };
  }

  if (turnState === "responding") {
    return {
      label: "AIが話しています",
      className: "rounded-full bg-[#191c1b] px-3 py-1 text-xs font-semibold text-white",
    };
  }

  return null;
}

function resolveAgentTransportErrorMessage(message: string): string {
  const normalized = message.toLowerCase();
  if (
    normalized.includes("1007") ||
    normalized.includes("token") ||
    normalized.includes("gemini-live-1007")
  ) {
    return "面談セッションの上限に達したため、音声接続を再接続しています。";
  }

  return "音声接続が切れました。再接続してください。";
}

function isProviderFatalCloseReason(reason: string | null): boolean {
  return reason?.startsWith("gemini-live-") ?? false;
}

function resolveTransportRecoveryCause(
  source: "close" | "error",
  closeReason: string | null,
  providerFatalReason: string | null
): TransportRecoveryCause {
  if (providerFatalReason) {
    return { type: "provider-fatal", reason: providerFatalReason };
  }

  if (source === "close") {
    return { type: "socket-close", reason: closeReason };
  }

  return { type: "socket-error" };
}

const FRESH_SESSION_FORCE_AFTER_ATTEMPT = 2;

export function shouldRequestFreshAgentSession(
  cause: TransportRecoveryCause,
  attempt: number
): boolean {
  if (cause.type === "provider-fatal") return true;
  if (cause.type === "socket-close" && isProviderFatalCloseReason(cause.reason)) return true;
  if (cause.type === "socket-error") return true;
  if (attempt >= FRESH_SESSION_FORCE_AFTER_ATTEMPT) return true;
  return false;
}

async function resolveAgentSessionForRecovery(
  interviewId: string,
  currentSession: AgentSessionResponse,
  requestFreshSession: boolean
): Promise<AgentSessionResponse> {
  if (requestFreshSession) {
    return requestAgentSession(interviewId);
  }

  return currentSession;
}

function delay(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function encodePcmFrameToBase64(frame: ArrayBuffer): string {
  const bytes = new Uint8Array(frame);
  let binary = "";
  for (let index = 0; index < bytes.byteLength; index += 1) {
    binary += String.fromCharCode(bytes[index] ?? 0);
  }
  return typeof btoa !== "undefined" ? btoa(binary) : "";
}

export function resolveViewState(detail: InterviewDetail | null, overrideLive: boolean): ViewState {
  if (!detail) return "loading";
  if (overrideLive) return "live";
  const status = detail.interview.status;
  if (status === "COMPLETED" || status === "EVALUATING") return "completed";
  if (status === "LIVE") return "live";
  return "ready";
}

export function InterviewPage() {
  const { interviewId } = useParams({ strict: false }) as { interviewId: string };
  const debugEnabled = useMemo(() => readDebugFlag(), []);

  const [detail, setDetail] = useState<InterviewDetail | null>(null);
  const [liveActive, setLiveActive] = useState(false);
  const [completionSubmitted, setCompletionSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<AgentSessionResponse | null>(null);
  const [micState, setMicState] = useState<
    "idle" | "requesting" | "capturing" | "stopped" | "error"
  >("idle");
  const [playbackState, setPlaybackState] = useState<PlaybackState>("idle");
  const [connecting, setConnecting] = useState(false);
  const [uploadingRecording, setUploadingRecording] = useState(false);
  const [autoStartRequested, setAutoStartRequested] = useState(() =>
    hasPendingInterviewAutoStart(interviewId)
  );
  const [browserAudioDebug, setBrowserAudioDebug] =
    useState<LiveAudioDebugState>(DEFAULT_AUDIO_DEBUG_STATE);
  const [agentTransportDebug, setAgentTransportDebug] = useState<AgentLiveClientDebugState>(
    DEFAULT_AGENT_TRANSPORT_DEBUG_STATE
  );
  const [playbackDebug, setPlaybackDebug] = useState<AudioPlaybackDebugState>(
    DEFAULT_PLAYBACK_DEBUG_STATE
  );
  const [agentDebug, setAgentDebug] = useState<AgentDebugResponse | null>(null);
  const [candidateTurnState, setCandidateTurnState] = useState<CandidateTurnState>("idle");
  const [candidateDoneSubmitted, setCandidateDoneSubmitted] = useState(false);
  const [transportRecoveryState, setTransportRecoveryState] =
    useState<TransportRecoveryState>("idle");
  const [transportReconnectAttempts, setTransportReconnectAttempts] = useState(0);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [liveCaptions, setLiveCaptions] = useState<{ ai: string; candidate: string }>({
    ai: "",
    candidate: "",
  });
  const [micPaused, setMicPaused] = useState(false);

  const agentClientRef = useRef<AgentLiveClient | null>(null);
  const micRef = useRef<LiveAudioInput | null>(null);
  const playbackRef = useRef<AudioPlaybackQueue | null>(null);
  const recorderRef = useRef<InterviewRecorder | null>(null);
  const recordingStartedAtRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const autoStartResolvedForRef = useRef<string | null>(null);
  const candidateTurnHasAudioRef = useRef(false);
  const candidateDoneSubmittedRef = useRef(false);
  const candidateTurnStateRef = useRef<CandidateTurnState>("idle");
  const aiPlaybackActiveRef = useRef(false);
  const liveActiveRef = useRef(false);
  const recoveringTransportRef = useRef(false);
  const tearDownRef = useRef(false);
  const endingRef = useRef(false);
  const lastAgentFatalReasonRef = useRef<string | null>(null);
  const lastTransportRecoveryCauseRef = useRef<TransportRecoveryCause | null>(null);
  const recoverAgentTransportRef = useRef<
    ((nextSession: AgentSessionResponse, cause: TransportRecoveryCause) => void) | null
  >(null);
  const endLiveRef = useRef<(() => Promise<void>) | null>(null);
  const interviewCompletedEventSentRef = useRef(false);
  const micPausedRef = useRef(false);

  const refreshDetail = useCallback(async () => {
    try {
      const next = await fetchInterviewDetail(interviewId);
      setDetail(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : "failed to load interview");
    }
  }, [interviewId]);

  useEffect(() => {
    void refreshDetail();
  }, [refreshDetail]);

  useEffect(() => {
    setCompletionSubmitted(false);
    interviewCompletedEventSentRef.current = false;
  }, [interviewId]);

  useEffect(() => {
    liveActiveRef.current = liveActive;
  }, [liveActive]);

  useEffect(() => {
    if (!liveActive) {
      return undefined;
    }
    setElapsedSec(0);
    const timer = window.setInterval(() => {
      setElapsedSec((current) => current + 1);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [liveActive]);

  // セッション状態の生存監視: live 中は定期的に heartbeat を送って lastHeartbeatAt を実時刻に保ち、
  // タブ離脱時は connected:false を keepalive 送信して接続断を記録する。
  useEffect(() => {
    if (!liveActive) {
      return undefined;
    }
    void postHeartbeat(interviewId, true);
    const timer = window.setInterval(() => {
      void postHeartbeat(interviewId, true);
    }, HEARTBEAT_INTERVAL_MS);
    const handlePageHide = () => {
      void postHeartbeat(interviewId, false);
    };
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [interviewId, liveActive]);

  useEffect(() => {
    candidateTurnStateRef.current = candidateTurnState;
  }, [candidateTurnState]);

  const updateCandidateTurnState = useCallback(
    (nextState: CandidateTurnState | ((current: CandidateTurnState) => CandidateTurnState)) => {
      setCandidateTurnState((current) => {
        const resolvedState = typeof nextState === "function" ? nextState(current) : nextState;
        candidateTurnStateRef.current = resolvedState;
        return resolvedState;
      });
    },
    []
  );

  const refreshAgentDebug = useCallback(async () => {
    if (!debugEnabled) {
      return;
    }
    try {
      const next = await fetchAgentDebug(interviewId);
      setAgentDebug(next);
    } catch (err) {
      setAgentDebug({
        ok: true,
        available: false,
        state: "unavailable",
        debug: null,
        message: err instanceof Error ? err.message : "agent debug unavailable",
      });
    }
  }, [debugEnabled, interviewId]);

  useEffect(() => {
    if (!detail) return;
    const status = detail.interview.status;
    if (status === "EVALUATING" || status === "LIVE") {
      const timer = window.setInterval(refreshDetail, 3000);
      return () => window.clearInterval(timer);
    }
    return undefined;
  }, [detail, refreshDetail]);

  const connectAgentTransport = useCallback(async (nextSession: AgentSessionResponse) => {
    const client = new AgentLiveClient({
      session: nextSession,
      onAudioFrame: (frame) => {
        const base64 = encodePcmFrameToBase64(frame);
        void playbackRef.current?.enqueue({ data: base64, mimeType: "audio/pcm;rate=24000" });
      },
      onFatalError: (message) => {
        lastAgentFatalReasonRef.current = message;
        setError(resolveAgentTransportErrorMessage(message));
      },
      onClose: (event) => {
        if (
          tearDownRef.current ||
          endingRef.current ||
          recoveringTransportRef.current ||
          !liveActiveRef.current
        ) {
          return;
        }
        const closeReason = event.reason || null;
        const providerFatalReason = lastAgentFatalReasonRef.current;
        lastAgentFatalReasonRef.current = null;
        const cause = resolveTransportRecoveryCause("close", closeReason, providerFatalReason);
        const errorReason = providerFatalReason ?? closeReason;
        if (errorReason) {
          setError(resolveAgentTransportErrorMessage(errorReason));
        }
        recoverAgentTransportRef.current?.(nextSession, cause);
      },
      onError: () => {
        if (
          tearDownRef.current ||
          endingRef.current ||
          recoveringTransportRef.current ||
          !liveActiveRef.current
        ) {
          return;
        }
        const providerFatalReason = lastAgentFatalReasonRef.current;
        lastAgentFatalReasonRef.current = null;
        if (providerFatalReason) {
          setError(resolveAgentTransportErrorMessage(providerFatalReason));
        }
        recoverAgentTransportRef.current?.(
          nextSession,
          resolveTransportRecoveryCause("error", null, providerFatalReason)
        );
      },
      onDebugStateChange: setAgentTransportDebug,
      onTranscript: (speaker, text, final) => {
        setLiveCaptions((prev) =>
          speaker === "INTERVIEWER"
            ? { ...prev, ai: text }
            : { ...prev, candidate: final && text.length === 0 ? "" : text }
        );
      },
      onInterviewEnded: () => {
        if (endingRef.current || tearDownRef.current || !liveActiveRef.current) {
          return;
        }
        void endLiveRef.current?.();
      },
    });
    agentClientRef.current = client;
    await client.connect();
    setTransportRecoveryState("idle");
    setTransportReconnectAttempts(0);
  }, []);

  const recoverAgentTransport = useCallback(
    async (currentSession: AgentSessionResponse, cause: TransportRecoveryCause) => {
      if (recoveringTransportRef.current || tearDownRef.current || endingRef.current) {
        return;
      }

      lastTransportRecoveryCauseRef.current = cause;
      recoveringTransportRef.current = true;
      setTransportRecoveryState("reconnecting");
      micRef.current?.pause();

      const existing = agentClientRef.current;
      agentClientRef.current = null;
      existing?.close();

      let activeSession = currentSession;
      for (let attempt = 1; attempt <= AGENT_TRANSPORT_RECONNECT_DELAYS_MS.length; attempt += 1) {
        setTransportReconnectAttempts(attempt);
        try {
          const requestFreshSession = shouldRequestFreshAgentSession(cause, attempt);
          const nextSession = await resolveAgentSessionForRecovery(
            interviewId,
            activeSession,
            requestFreshSession
          );
          activeSession = nextSession;
          await connectAgentTransport(nextSession);
          setSession(nextSession);
          if (!micPausedRef.current) {
            micRef.current?.resume();
            micRef.current?.holdChunksUntilSpeechStart();
          }
          setError(null);
          recoveringTransportRef.current = false;
          await refreshAgentDebug();
          return;
        } catch {
          agentClientRef.current = null;
          if (attempt === AGENT_TRANSPORT_RECONNECT_DELAYS_MS.length) {
            setTransportRecoveryState("failed");
            setError("音声接続が切れました。再接続してください。");
            recoveringTransportRef.current = false;
            await refreshAgentDebug();
            return;
          }
          await delay(AGENT_TRANSPORT_RECONNECT_DELAYS_MS[attempt - 1] ?? 5000);
        }
      }
    },
    [connectAgentTransport, interviewId, refreshAgentDebug]
  );

  recoverAgentTransportRef.current = (nextSession, cause) => {
    void recoverAgentTransport(nextSession, cause);
  };

  const tearDown = useCallback(async () => {
    tearDownRef.current = true;
    recoveringTransportRef.current = false;
    agentClientRef.current?.close();
    agentClientRef.current = null;

    if (micRef.current) {
      await micRef.current.stop();
      micRef.current = null;
    }

    if (playbackRef.current) {
      await playbackRef.current.close();
      playbackRef.current = null;
    }

    if (recorderRef.current) {
      await recorderRef.current.close();
      recorderRef.current = null;
    }

    if (audioContextRef.current) {
      if (audioContextRef.current.state !== "closed") {
        await audioContextRef.current.close();
      }
      audioContextRef.current = null;
    }

    setLiveActive(false);
    setUploadingRecording(false);
    setSession(null);
    setMicState("idle");
    setPlaybackState("idle");
    setBrowserAudioDebug(DEFAULT_AUDIO_DEBUG_STATE);
    setAgentTransportDebug(DEFAULT_AGENT_TRANSPORT_DEBUG_STATE);
    setPlaybackDebug(DEFAULT_PLAYBACK_DEBUG_STATE);
    updateCandidateTurnState("idle");
    setCandidateDoneSubmitted(false);
    setTransportRecoveryState("idle");
    setTransportReconnectAttempts(0);
    candidateTurnHasAudioRef.current = false;
    candidateDoneSubmittedRef.current = false;
    aiPlaybackActiveRef.current = false;
    lastAgentFatalReasonRef.current = null;
    lastTransportRecoveryCauseRef.current = null;
    setAgentDebug((current) =>
      current?.state === "active"
        ? {
            ok: true,
            available: false,
            state: "not-started",
            debug: null,
            message: "Agent session not started",
          }
        : current
    );
    liveActiveRef.current = false;
    tearDownRef.current = false;
  }, [updateCandidateTurnState]);

  const startLive = useCallback(async () => {
    if (connecting || liveActive) return;
    endingRef.current = false;
    tearDownRef.current = false;
    setConnecting(true);
    setError(null);
    try {
      await postJoin(interviewId);
      const nextSession = await requestAgentSession(interviewId);
      await postStart(interviewId);

      // 録音用に AI 再生・候補者マイク・録音を単一 AudioContext で共有し、
      // AI(TTS)+候補者音声をミックスして収録する。
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const recorder = new InterviewRecorder({ audioContext });
      recorderRef.current = recorder;

      const playback = new AudioPlaybackQueue({
        audioContext,
        additionalDestinations: [recorder.aiDestination],
        onDebugStateChange: setPlaybackDebug,
        onStateChange: setPlaybackState,
      });
      playbackRef.current = playback;
      await playback.resume();
      await connectAgentTransport(nextSession);

      const mic = new LiveAudioInput({
        audioContext,
        mixDestination: recorder.candidateDestination,
        onChunk: (chunk) => {
          if (
            aiPlaybackActiveRef.current ||
            candidateTurnStateRef.current === "processing" ||
            candidateTurnStateRef.current === "responding"
          ) {
            return;
          }
          candidateTurnHasAudioRef.current = true;
          agentClientRef.current?.sendAudio(chunk);
        },
        onSpeechStart: () => {
          if (
            aiPlaybackActiveRef.current ||
            candidateTurnStateRef.current === "processing" ||
            candidateTurnStateRef.current === "responding"
          ) {
            return;
          }
          candidateTurnHasAudioRef.current = false;
          candidateDoneSubmittedRef.current = false;
          setCandidateDoneSubmitted(false);
          agentClientRef.current?.sendActivityStart();
          updateCandidateTurnState("listening");
          setLiveCaptions((prev) => ({ ...prev, ai: "" }));
        },
        onSpeechEnd: () => {
          if (
            aiPlaybackActiveRef.current ||
            candidateTurnStateRef.current === "processing" ||
            candidateTurnStateRef.current === "responding"
          ) {
            return;
          }
          agentClientRef.current?.sendActivityEnd();
          mic.holdChunksUntilSpeechStart();
          updateCandidateTurnState(
            candidateTurnHasAudioRef.current || candidateDoneSubmittedRef.current
              ? "processing"
              : "idle"
          );
          candidateTurnHasAudioRef.current = false;
        },
        onStateChange: setMicState,
        ...(debugEnabled ? { onDebug: setBrowserAudioDebug } : {}),
      });
      micRef.current = mic;
      await mic.start();
      mic.holdChunksUntilSpeechStart();
      await recorder.start();
      recordingStartedAtRef.current = Date.now();

      setSession(nextSession);
      setLiveActive(true);
      liveActiveRef.current = true;
      await refreshAgentDebug();
      await refreshDetail();
    } catch (err) {
      await tearDown();
      setError(err instanceof Error ? err.message : "failed to start interview");
    } finally {
      setConnecting(false);
    }
  }, [
    connectAgentTransport,
    connecting,
    debugEnabled,
    interviewId,
    liveActive,
    refreshAgentDebug,
    refreshDetail,
    tearDown,
    updateCandidateTurnState,
  ]);

  const endLive = useCallback(async () => {
    endingRef.current = true;
    // 録音を確定してアップロード（best-effort: 失敗しても面談完了は継続する）
    try {
      setUploadingRecording(true);
      const recording = await recorderRef.current?.stop();
      if (recording && recording.size > 0) {
        const startedAt = recordingStartedAtRef.current;
        const durationMs = startedAt !== null ? Math.max(0, Date.now() - startedAt) : null;
        await postRecording(interviewId, recording.blob, recording.mimeType, durationMs);
      }
      recordingStartedAtRef.current = null;
    } catch (err) {
      console.error("interview recording upload failed", err);
    } finally {
      setUploadingRecording(false);
    }
    try {
      await postComplete(interviewId);
      pushInterviewCompletedEventOnce(interviewCompletedEventSentRef);
      setCompletionSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "failed to complete");
    }
    await tearDown();
    endingRef.current = false;
    await refreshDetail();
  }, [interviewId, refreshDetail, tearDown]);

  endLiveRef.current = endLive;

  useEffect(() => {
    return () => {
      void tearDown();
    };
  }, [tearDown]);

  useEffect(() => {
    if (autoStartResolvedForRef.current === interviewId) {
      return;
    }

    autoStartResolvedForRef.current = interviewId;

    if (!consumePendingInterviewAutoStart(interviewId)) {
      setAutoStartRequested(false);
    }
  }, [interviewId]);

  const resolvedView = resolveViewState(detail, liveActive);
  const view = completionSubmitted ? "completed" : resolvedView;
  const isCompletionView = view === "completed";
  const immersiveLiveView =
    view === "live" || (autoStartRequested && (view === "loading" || view === "ready"));
  const templateSnapshot = detail?.interview.templateSnapshotJson;
  const templateTitle = templateSnapshot?.title ?? "面談";
  const questionPlan = templateSnapshot?.questionPlan ?? [];
  const candidateLabel =
    detail?.candidateProfile.name ?? detail?.interview.candidateName ?? "あなた";
  const currentQuestion = useMemo(
    () => resolveCurrentQuestion(detail?.turns ?? [], questionPlan),
    [detail?.turns, questionPlan]
  );
  const latestTranscriptTurn = useMemo(
    () => resolveLatestTranscriptTurn(detail?.turns ?? []),
    [detail?.turns]
  );
  const showAudioUnlock = immersiveLiveView && !liveActive;
  const audioStatusLabel = resolveAudioStatusLabel(
    playbackState,
    connecting,
    liveActive,
    showAudioUnlock,
    transportRecoveryState
  );
  const candidateTurnStatus = resolveCandidateTurnStatus(candidateTurnState);
  const canSubmitCandidateDone = candidateTurnState === "listening" && !candidateDoneSubmitted;

  useEffect(() => {
    if (playbackState === "playing") {
      aiPlaybackActiveRef.current = true;
      micRef.current?.pause();
      candidateDoneSubmittedRef.current = false;
      setCandidateDoneSubmitted(false);
      updateCandidateTurnState("responding");
      setLiveCaptions((prev) => ({ ...prev, candidate: "" }));
      return;
    }

    if (playbackState === "idle") {
      const shouldResumeMic = aiPlaybackActiveRef.current;
      aiPlaybackActiveRef.current = false;
      if (
        shouldResumeMic &&
        liveActiveRef.current &&
        !recoveringTransportRef.current &&
        !endingRef.current &&
        !tearDownRef.current &&
        !micPausedRef.current
      ) {
        micRef.current?.resume();
        micRef.current?.holdChunksUntilSpeechStart();
      }
      candidateDoneSubmittedRef.current = false;
      setCandidateDoneSubmitted(false);
      updateCandidateTurnState((current) => (current === "responding" ? "idle" : current));
    }
  }, [playbackState, updateCandidateTurnState]);

  useEffect(() => {
    if (!debugEnabled || !immersiveLiveView) {
      return undefined;
    }

    void refreshAgentDebug();
    const timer = window.setInterval(() => {
      void refreshAgentDebug();
    }, 1000);
    return () => window.clearInterval(timer);
  }, [debugEnabled, immersiveLiveView, refreshAgentDebug]);

  const handleUnlockAudio = useCallback(async () => {
    try {
      await playbackRef.current?.resume();
      setPlaybackState("idle");
      setError(null);
    } catch {
      setPlaybackState("error");
    }
  }, []);

  const handleCandidateDone = useCallback(() => {
    if (candidateTurnState !== "listening" || candidateDoneSubmittedRef.current) {
      return;
    }
    candidateDoneSubmittedRef.current = true;
    setCandidateDoneSubmitted(true);
    agentClientRef.current?.sendActivityEnd();
    micRef.current?.holdChunksUntilSpeechStart();
    agentClientRef.current?.sendCandidateDone("manual_button");
    updateCandidateTurnState("processing");
  }, [candidateTurnState, updateCandidateTurnState]);

  const handleTogglePause = useCallback(() => {
    const next = !micPausedRef.current;
    micPausedRef.current = next;
    setMicPaused(next);
    if (next) {
      micRef.current?.pause();
    } else {
      micRef.current?.resume();
    }
  }, []);

  if (immersiveLiveView) {
    const aiSpeaking = playbackState === "playing" || candidateTurnState === "responding";
    const youSpeaking = candidateTurnState === "listening";
    const connectionLabel =
      transportRecoveryState === "reconnecting"
        ? "再接続中"
        : transportRecoveryState === "failed"
          ? "接続が不安定"
          : "接続良好";
    return (
      <section data-testid="interview-live" className="iv-room">
        <div className="iv-global-header">
          <Header mode="public" />
        </div>
        <div className="roombar">
          <div className="rb-right">
            <span className="timer">
              {pad(Math.floor(elapsedSec / 60))}:{pad(elapsedSec % 60)}
            </span>
            <span className={`conn ${transportRecoveryState === "idle" ? "" : "conn-warn"}`}>
              <span className="conn-dot" />
              {connectionLabel}
            </span>
            <span data-testid="interview-audio-state" className="sr-only">
              {audioStatusLabel}
            </span>
            <span data-testid="interview-mic-state" className="sr-only">
              mic: {micState}
            </span>
          </div>
        </div>

        <div className={`stage ${aiSpeaking ? "talking" : "listening"}`}>
          <div className="stage-grid" />

          <div className="iv-topcenter">
            {liveCaptions.candidate && (
              <div data-testid="interview-live-captions" className="iv-captions">
                <p className="iv-cap iv-cap-you">
                  <span className="iv-cap-spk">あなた</span>
                  <span>{liveCaptions.candidate}</span>
                </p>
              </div>
            )}
            {candidateTurnStatus && (
              <div data-testid="interview-candidate-turn-state" className="iv-turn">
                {candidateTurnStatus.label}
              </div>
            )}
            {error && (
              <div data-testid="interview-error" className="iv-banner iv-banner-error">
                {error}
              </div>
            )}
            {transportRecoveryState === "reconnecting" && (
              <div className="iv-banner iv-banner-info">
                <p data-testid="interview-transport-reconnecting">
                  音声接続を再接続中です。少し待つと会話が再開します。
                </p>
              </div>
            )}
            {transportRecoveryState === "failed" && (
              <div className="iv-banner iv-banner-warn">
                <p data-testid="interview-transport-reconnect-failed">
                  音声接続が切れました。再接続してください。
                </p>
                <button
                  type="button"
                  data-testid="interview-transport-retry"
                  onClick={() => {
                    if (session) {
                      void recoverAgentTransport(
                        session,
                        lastTransportRecoveryCauseRef.current ?? {
                          type: "socket-close",
                          reason: null,
                        }
                      );
                    }
                  }}
                  className="iv-banner-btn"
                >
                  音声接続を再試行
                </button>
              </div>
            )}
            {playbackState === "error" && (
              <div className="iv-banner iv-banner-warn">
                <p data-testid="interview-audio-error">
                  音声再生の初期化に失敗しました。ブラウザの音声を再有効化してください。
                </p>
                <button
                  type="button"
                  onClick={() => {
                    void handleUnlockAudio();
                  }}
                  className="iv-banner-btn"
                >
                  音声を再有効化
                </button>
              </div>
            )}
          </div>

          <div className="orb-area">
            <div className="orb-wrap">
              <span className="ring r1" />
              <span className="ring r2" />
              <span className="ring r3" />
              <div className="orb">
                {aiSpeaking ? (
                  <div className="orb-eq">
                    {[0, 1, 2, 3, 4].map((bar) => (
                      <i
                        key={bar}
                        style={{
                          animationDelay: `${bar * 0.12}s`,
                          animationDuration: `${0.7 + bar * 0.1}s`,
                        }}
                      />
                    ))}
                  </div>
                ) : (
                  <IconBot width={56} height={56} aria-hidden="true" />
                )}
              </div>
              <span className="orb-caption">
                {aiSpeaking ? "AI が質問しています" : "あなたの番です"}
              </span>
            </div>
          </div>

          <div className="panel ask">
            <div className="ask-tag">
              <span className="tdot" />
              NOW ASKING
            </div>
            <h2 data-testid="interview-current-question" className="ask-q">
              {currentQuestion}
            </h2>
          </div>

          <div className="badge">
            <div className="badge-eq">
              {[14, 22, 10, 18, 12].map((barHeight, bar) => (
                <i
                  key={barHeight}
                  style={{ height: `${barHeight}px`, animationDelay: `${bar * 0.13}s` }}
                />
              ))}
            </div>
            <div className="badge-tx">
              <div className="bt">{AI_DISPLAY_NAME}</div>
              <div className="bs">{AI_DISPLAY_ROLE}</div>
            </div>
          </div>

          <div className={`you ${youSpeaking ? "speaking" : ""}`}>
            <div className="you-avatar">
              <IconUser width={56} height={56} aria-hidden="true" />
            </div>
            <div className="you-mic">
              <IconMic width={16} height={16} aria-hidden="true" />
            </div>
            <div className="you-level">
              <i />
            </div>
            <div className="you-label">
              <div className="yl">YOU</div>
              <div className="yn">{candidateLabel}</div>
            </div>
          </div>

          {latestTranscriptTurn?.speaker === "CANDIDATE" && (
            <div data-testid="interview-caption" className="cc">
              <b>YOU</b>
              {latestTranscriptTurn.transcript}
            </div>
          )}

          {showAudioUnlock && (
            <div className="iv-unlock">
              <div className="iv-unlock-card">
                <p className="iv-unlock-tag">AUDIO UNLOCK</p>
                <h3 className="iv-unlock-title">音声を有効化して面談を開始</h3>
                <p className="iv-unlock-desc">
                  面談 UI は表示済みですが、最初の AI
                  音声を確実に再生するために一度だけ開始操作が必要です。
                </p>
                <button
                  type="button"
                  data-testid="interview-audio-unlock"
                  onClick={() => {
                    void startLive();
                  }}
                  disabled={connecting}
                  className="iv-unlock-btn"
                >
                  {connecting ? "接続中..." : "音声を有効化して面談を開始"}
                </button>
              </div>
            </div>
          )}
        </div>

        {debugEnabled && (
          <section
            data-testid="interview-debug-panel"
            className="mx-auto mt-4 grid w-full max-w-7xl gap-4 rounded-[28px] border border-[#d6e8cd] bg-[#101817] p-5 text-white shadow-sm"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-bold tracking-[0.22em] text-[#bcf0ae]">DEBUG MODE</p>
                <p className="mt-1 text-sm text-white/75">
                  候補者音声の認識と転送経路を表示します。
                </p>
              </div>
              <div
                data-testid="interview-debug-agent-state"
                className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold"
              >
                agent: {agentDebug?.state ?? "loading"}
              </div>
            </div>

            <div className="grid gap-4 xl:grid-cols-3">
              <DebugCard title="Browser Mic">
                <DebugRow label="mic" value={browserAudioDebug.micState} />
                <DebugRow label="speechActive" value={String(browserAudioDebug.speechActive)} />
                <DebugRow
                  label="holding"
                  value={String(browserAudioDebug.holdingUntilSpeechStart)}
                />
                <DebugRow label="peak" value={browserAudioDebug.peak.toFixed(4)} />
                <DebugRow label="rms" value={browserAudioDebug.rms.toFixed(4)} />
                <DebugRow
                  label="chunksSent"
                  value={String(browserAudioDebug.forwardedChunkCount)}
                />
                <DebugRow
                  label="lastChunk"
                  value={formatDebugTimestamp(browserAudioDebug.lastForwardedAt)}
                />
              </DebugCard>

              <DebugCard title="WebSocket / Playback">
                <DebugRow label="ws" value={agentTransportDebug.wsState} />
                <DebugRow label="recovery" value={transportRecoveryState} />
                <DebugRow label="retries" value={String(transportReconnectAttempts)} />
                <DebugRow label="frames" value={String(agentTransportDebug.audioFramesReceived)} />
                <DebugRow label="wsBytes" value={String(agentTransportDebug.audioBytesReceived)} />
                <DebugRow
                  label="lastFrame"
                  value={formatDebugTimestamp(agentTransportDebug.lastAudioFrameAt)}
                />
                <DebugRow
                  label="lastFrameBytes"
                  value={String(agentTransportDebug.lastAudioFrameByteLength ?? 0)}
                />
                <DebugRow label="messageKind" value={agentTransportDebug.lastMessageKind} />
                <DebugRow label="playback" value={playbackState} />
                <DebugRow label="playbackEnqueue" value={String(playbackDebug.enqueueCount)} />
                <DebugRow label="playbackDecode" value={String(playbackDebug.decodeCount)} />
                <DebugRow label="playbackStart" value={String(playbackDebug.startCount)} />
                <DebugRow label="playbackEnded" value={String(playbackDebug.endedCount)} />
                <DebugRow label="playbackErrors" value={String(playbackDebug.errorCount)} />
                <DebugRow label="playbackActive" value={String(playbackDebug.activeSourceCount)} />
                <DebugRow label="audioCtx" value={playbackDebug.audioContextState} />
                <DebugRow label="wsError" value={agentTransportDebug.lastError ?? "--"} />
              </DebugCard>

              <DebugCard title="Agent Runtime">
                <DebugRow
                  label="attached"
                  value={String(agentDebug?.debug?.browserAudioAttached ?? false)}
                />
                <DebugRow
                  label="lastAttached"
                  value={formatDebugTimestamp(
                    agentDebug?.debug?.lastBrowserAudioAttachedAt ?? null
                  )}
                />
                <DebugRow
                  label="lastDetached"
                  value={formatDebugTimestamp(
                    agentDebug?.debug?.lastBrowserAudioDetachedAt ?? null
                  )}
                />
                <DebugRow
                  label="setup"
                  value={String(agentDebug?.debug?.geminiSetupComplete ?? false)}
                />
                <DebugRow
                  label="bootstrap"
                  value={String(agentDebug?.debug?.bootstrapSent ?? false)}
                />
                <DebugRow
                  label="bootstrapTransport"
                  value={agentDebug?.debug?.bootstrapTransport ?? "--"}
                />
                <DebugRow
                  label="connectAttempt"
                  value={String(agentDebug?.debug?.connectAttempt ?? 0)}
                />
                <DebugRow
                  label="awaitingFirst"
                  value={String(agentDebug?.debug?.awaitingFirstModelResponse ?? false)}
                />
                <DebugRow
                  label="bootstrapTimedOut"
                  value={String(agentDebug?.debug?.bootstrapTimedOut ?? false)}
                />
                <DebugRow
                  label="inbound"
                  value={String(agentDebug?.debug?.inboundAudioCount ?? 0)}
                />
                <DebugRow
                  label="aiFrames"
                  value={String(agentDebug?.debug?.aiAudioFrameCount ?? 0)}
                />
                <DebugRow
                  label="outboundFrames"
                  value={String(agentDebug?.debug?.outboundAudioFrameCount ?? 0)}
                />
                <DebugRow
                  label="outboundBytes"
                  value={String(agentDebug?.debug?.outboundAudioBytes ?? 0)}
                />
                <DebugRow
                  label="dailySent"
                  value={String(agentDebug?.debug?.dailyBot?.outboundAudioSentCount ?? 0)}
                />
                <DebugRow
                  label="dailyBytes"
                  value={String(agentDebug?.debug?.dailyBot?.outboundAudioSentBytes ?? 0)}
                />
                <DebugRow
                  label="dailyBacklog"
                  value={String(agentDebug?.debug?.dailyBot?.outboundAudioBacklogLength ?? 0)}
                />
                <DebugRow
                  label="messages"
                  value={String(agentDebug?.debug?.geminiMessageCount ?? 0)}
                />
              </DebugCard>
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              <DebugCard title="Candidate Activity">
                <DebugRow
                  label="activityState"
                  value={agentDebug?.debug?.candidateActivityState ?? "idle"}
                />
                <DebugRow
                  label="lastActivityStart"
                  value={formatDebugTimestamp(agentDebug?.debug?.lastActivityStartAt ?? null)}
                />
                <DebugRow
                  label="lastActivityEnd"
                  value={formatDebugTimestamp(agentDebug?.debug?.lastActivityEndAt ?? null)}
                />
                <DebugRow
                  label="lastActivitySignal"
                  value={formatDebugTimestamp(agentDebug?.debug?.lastActivitySignalAt ?? null)}
                />
              </DebugCard>

              <DebugCard title="Agent Timestamps">
                <DebugRow
                  label="bootstrapSentAt"
                  value={formatDebugTimestamp(agentDebug?.debug?.bootstrapSentAt ?? null)}
                />
                <DebugRow
                  label="lastInbound"
                  value={formatDebugTimestamp(agentDebug?.debug?.lastInboundAudioAt ?? null)}
                />
                <DebugRow label="config" value={agentDebug?.debug?.configProfile ?? "--"} />
                <DebugRow
                  label="lastInputTx"
                  value={formatDebugTimestamp(agentDebug?.debug?.lastInputTranscriptionAt ?? null)}
                />
                <DebugRow
                  label="lastOutputTx"
                  value={formatDebugTimestamp(agentDebug?.debug?.lastOutputTranscriptionAt ?? null)}
                />
                <DebugRow
                  label="lastGemini"
                  value={formatDebugTimestamp(agentDebug?.debug?.lastGeminiMessageAt ?? null)}
                />
                <DebugRow
                  label="lastAiFrame"
                  value={formatDebugTimestamp(agentDebug?.debug?.lastAiAudioFrameAt ?? null)}
                />
                <DebugRow
                  label="lastOutbound"
                  value={formatDebugTimestamp(agentDebug?.debug?.lastOutboundAudioAt ?? null)}
                />
                <DebugRow
                  label="outboundFrameBytes"
                  value={String(agentDebug?.debug?.lastOutboundAudioByteLength ?? 0)}
                />
                <DebugRow
                  label="dailyLastSent"
                  value={formatDebugTimestamp(
                    agentDebug?.debug?.dailyBot?.lastOutboundAudioSentAt ?? null
                  )}
                />
                <DebugRow
                  label="lastGoAway"
                  value={formatDebugTimestamp(agentDebug?.debug?.lastGoAwayAt ?? null)}
                />
                <DebugRow
                  label="goAwayMs"
                  value={String(agentDebug?.debug?.lastGoAwayTimeLeftMs ?? 0)}
                />
                <DebugRow
                  label="resumeHandle"
                  value={String(agentDebug?.debug?.resumeHandlePresent ?? false)}
                />
                <DebugRow
                  label="resumeHandleAt"
                  value={formatDebugTimestamp(agentDebug?.debug?.lastResumeHandleUpdatedAt ?? null)}
                />
                <DebugRow label="resumption" value={agentDebug?.debug?.resumptionState ?? "idle"} />
              </DebugCard>

              <DebugCard title="Agent Flags">
                <DebugRow
                  label="hasInputTx"
                  value={String(
                    agentDebug?.debug?.lastMessageFlags?.hasInputTranscription ?? false
                  )}
                />
                <DebugRow
                  label="hasOutputTx"
                  value={String(
                    agentDebug?.debug?.lastMessageFlags?.hasOutputTranscription ?? false
                  )}
                />
                <DebugRow
                  label="hasModelTurn"
                  value={String(agentDebug?.debug?.lastMessageFlags?.hasModelTurn ?? false)}
                />
                <DebugRow
                  label="turnComplete"
                  value={String(agentDebug?.debug?.lastMessageFlags?.turnComplete ?? false)}
                />
                <DebugRow
                  label="lastError"
                  value={agentDebug?.debug?.lastError ?? agentDebug?.message ?? "--"}
                />
              </DebugCard>
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              <DebugTranscriptCard
                dataTestId="interview-debug-candidate"
                title="Candidate Partial"
                value={agentDebug?.debug?.pendingCandidateTranscript ?? ""}
              />
              <DebugTranscriptCard
                dataTestId="interview-debug-interviewer"
                title="Interviewer Partial"
                value={agentDebug?.debug?.pendingInterviewerTranscript ?? ""}
              />
            </div>
          </section>
        )}
        <footer data-testid="interview-footer" className="controls">
          {canSubmitCandidateDone && (
            <button
              type="button"
              data-testid="interview-answer-done"
              onClick={handleCandidateDone}
              className="ctl primary"
            >
              回答を完了
            </button>
          )}
          {liveActive && (
            <button
              type="button"
              data-testid="interview-pause"
              onClick={handleTogglePause}
              disabled={showAudioUnlock || uploadingRecording}
              aria-pressed={micPaused}
              className="ctl"
            >
              {micPaused ? "発話を再開する" : "一時停止"}
            </button>
          )}
          <button
            type="button"
            onClick={endLive}
            disabled={connecting || showAudioUnlock || uploadingRecording}
            data-testid="interview-end"
            className="ctl danger"
          >
            <IconEnd width={18} height={18} aria-hidden="true" />
            {uploadingRecording ? "録音を保存中..." : "面談を終了する"}
          </button>
        </footer>
      </section>
    );
  }

  return (
    <div className={isCompletionView ? "iv-completion-page" : "min-h-screen bg-white"}>
      {!isCompletionView && <Header mode="public" />}
      <main
        className={
          isCompletionView
            ? "iv-completion-main"
            : "mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-8"
        }
      >
        {error && (
          <div
            data-testid="interview-error"
            className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700"
          >
            {error}
          </div>
        )}

        {view === "loading" && <div data-testid="interview-loading">読み込み中...</div>}

        {view === "ready" && detail && (
          <section data-testid="interview-ready" className="flex flex-col gap-4">
            <h1 className="text-2xl font-bold">{templateTitle}</h1>
            <p className="text-sm text-slate-600">候補者: {detail.candidateProfile.name ?? "-"}</p>
            <div
              data-testid="interview-ready-guide"
              className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700"
            >
              <p className="font-semibold text-slate-900">面談について</p>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>所要時間: 約15分</li>
                <li>ご提出いただいた職務経歴書を踏まえて質問します</li>
                <li>音声でAIとやり取りします。落ち着いてお話しください</li>
              </ul>
              {questionPlan.length > 0 && (
                <>
                  <p className="mt-3 font-semibold text-slate-900">
                    本日伺うテーマ（全 {questionPlan.length} 問）
                  </p>
                  <ol className="mt-2 list-decimal space-y-1 pl-5">
                    {questionPlan.map((q, index) => (
                      <li key={`${index}-${q.slice(0, 12)}`}>{q}</li>
                    ))}
                  </ol>
                </>
              )}
            </div>
            <button
              type="button"
              onClick={startLive}
              disabled={connecting}
              aria-busy={connecting}
              data-testid="interview-start"
              className="inline-flex w-fit items-center justify-center gap-2 rounded bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {connecting && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
              {connecting ? "面談を開始しています..." : "面談を開始する"}
            </button>
          </section>
        )}

        {view === "completed" && (
          <section data-testid="interview-completed" className="iv-done">
            <div className="iv-done-card">
              <div className="iv-done-burst">
                <IconCheck width={38} height={38} aria-hidden="true" />
              </div>
              <h1 className="iv-done-title">面談が完了しました</h1>
              <p className="iv-done-text">
                {candidateLabel}{" "}
                様、お疲れさまでした。結果は担当者の確認後、メールにてご連絡いたします。
              </p>
              <div className="iv-done-stats">
                <div className="iv-done-stat">
                  <div className="dv">
                    {pad(Math.floor(elapsedSec / 60))}:{pad(elapsedSec % 60)}
                  </div>
                  <div className="dl">面談時間</div>
                </div>
                <div className="iv-done-stat">
                  <div className="dv">{questionPlan.length}</div>
                  <div className="dl">質問数</div>
                </div>
                <div className="iv-done-stat">
                  <div className="dv">100%</div>
                  <div className="dl">回答率</div>
                </div>
              </div>
              <PostInterviewCta />
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

function DebugCard({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
      <p className="mb-3 text-xs font-bold tracking-[0.2em] text-[#bcf0ae]">{title}</p>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function DebugRow({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-white/65">{label}</span>
      <span className="truncate text-right font-medium text-white">{value}</span>
    </div>
  );
}

function DebugTranscriptCard({
  dataTestId,
  title,
  value,
}: {
  dataTestId: string;
  title: string;
  value: string;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
      <p className="mb-3 text-xs font-bold tracking-[0.2em] text-[#bcf0ae]">{title}</p>
      <p
        data-testid={dataTestId}
        className="min-h-16 whitespace-pre-wrap rounded-2xl bg-black/20 px-4 py-3 text-sm leading-6 text-white/90"
      >
        {value.length > 0 ? value : "まだ受信していません"}
      </p>
    </div>
  );
}
